import React, { useRef, useEffect } from "react";
import Chart from "chart.js";
import { DegradationChartDummyData } from "./dummyData/SingleDummyData";
import { Box } from "@material-ui/core";

/**
 * DegradationChart
 * @param {*} levelBands 損傷度レベル定義
 * @param {*} lineData 劣化曲線データ
 * @param {*} currentData Currentデータ
 * @param {*} predictionData Predictionデータ
 * @returns
 */
const DegradationChart = ({
  levelBands = DegradationChartDummyData.levelBands,
  lineData = DegradationChartDummyData.lineData,
  currentData = DegradationChartDummyData.currentData,
  predictionData = DegradationChartDummyData.predictionData,
}) => {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    try {
      const ctx = canvasRef.current.getContext("2d");
      if (chartInstanceRef.current) {
        chartInstanceRef.current.stop();
        chartInstanceRef.current.destroy();
      }

      levelBands = JSON.parse(JSON.stringify(levelBands));
      lineData = JSON.parse(JSON.stringify(lineData));
      currentData = JSON.parse(JSON.stringify(currentData));
      predictionData = JSON.parse(JSON.stringify(predictionData));

      chartInstanceRef.current = new Chart(ctx, {
        type: "line",
        data: {
          datasets: [
            {
              label: currentData[0]?.title || "",
              data: currentData,
              backgroundColor: currentData[0]?.backgroundColor || "",
              fill: false,
              pointRadius: 8,
              lineTension: 0.3,
              pointStyle: "rectRot",
            },
            {
              label: predictionData[0]?.title || "",
              data: predictionData,
              backgroundColor: predictionData[0]?.backgroundColor || "",
              fill: false,
              pointRadius: 8,
              lineTension: 0.3,
            },
            {
              data: lineData,
              borderColor: "#87cefa",
              fill: false,
              pointRadius: 0,
              lineTension: 0.3,
            },
          ],
        },
        options: {
          animation: {
            duration: 250,
          },
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              right: 110,
            },
          },
          legend: {
            labels: {
              fontSize: 11,
              position: "top",
              usePointStyle: true,
              pointStyle: "circle",
              filter: (legendItem) => legendItem.text !== "",
              generateLabels: (chart) => {
                try {
                  // 基準線の凡例はskip
                  const datasets = chart.data.datasets.filter(
                    (dataset, i) => i !== chart.data.datasets.length - 1
                  );
                  return datasets.map((dataset, i) => {
                      return {
                        text: dataset.label,
                        fillStyle: dataset.backgroundColor,
                        strokeStyle: dataset.pointBorderColor,
                        lineWidth: 1,
                        pointStyle: i == 0 ? "rectRot" : "circle",
                        hidden: !chart.isDatasetVisible(i),
                        datasetIndex: i,
                      };
                    });
                } catch (e) {
                  console.error(e);
                  return [];
                }
              },
            },
          },
          scales: {
            xAxes: [
              {
                type: "linear",
                scaleLabel: {
                  display: true,
                  labelString: "Age(Years)",
                },
                ticks: {
                  min: 0,
                  stepSize: 5,
                },
              },
            ],
            yAxes: [
              {
                ticks: {
                  min: 0,
                  max: 100,
                },
                scaleLabel: {
                  display: true,
                  labelString: "PCI",
                },
              },
            ],
          },
          tooltips: {
            callbacks: {
              title: (tooltipItem, data) => {
                return `PCI: ${tooltipItem[0].label}`;
              },
              label:  (tooltipItem, data) => {
                const tmpLabel = data.datasets[tooltipItem.datasetIndex]?.label || '';
                return `${tmpLabel}: ${tooltipItem.value}`;
              },
            },
          },
          plugins: {
            datalabels: {
              display: false,
            },
            outlabels: {
              display: false,
            },
          },
        },
        plugins: [
          {
            beforeDraw: (chart) => {
              try {
                const _ctx = chart.chart.ctx;
                const yScale = chart.scales["y-axis-0"];
                const xScale = chart.scales["x-axis-0"];
                const chartWidth = chart.chart.width;
                _ctx.save();
                levelBands.forEach((band) => {
                  const yTop = yScale.getPixelForValue(band.yMax);
                  const yBottom = yScale.getPixelForValue(band.yMin);
                  const bandHeight = yBottom - yTop + 3;

                  // 損傷度帯の背景色を描く
                  if (band.level == 3) {
                    _ctx.fillStyle = "rgba(255, 200, 156, 0.4)";
                    _ctx.fillRect(
                      xScale.left,
                      yTop,
                      chartWidth - xScale.left - 100,
                      bandHeight
                    );
                  } else if (band.level >= 4) {
                    _ctx.fillStyle = "rgba(255, 160, 163, 0.4)";
                    _ctx.fillRect(
                      xScale.left,
                      yTop,
                      chartWidth - xScale.left - 100,
                      bandHeight
                    );
                  }

                  // ラベル背景（帯と同じ高さ・位置、幅は適宜調整）
                  const labelX = chartWidth - 100;
                  const labelWidth = 90;
                  _ctx.fillStyle = band.color; // 帯と同じ色にする場合
                  _ctx.fillRect(labelX, yTop, labelWidth, bandHeight);

                  // ラベルテキストを矩形の中央に描画
                  _ctx.fillStyle = "black";
                  _ctx.font = "12px Arial";
                  _ctx.textAlign = "center";
                  _ctx.textBaseline = "middle";
                  _ctx.fillText(
                    band.label,
                    labelX + labelWidth / 2,
                    yTop + bandHeight / 2
                  );
                });
                _ctx.restore();
              } catch (e) {
                console.error(e);
              }
            },
          },
        ],
      });
    } catch (e) {
      console.error(e);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.stop();
        chartInstanceRef.current.destroy();
      }
    };
  }, [levelBands, lineData, currentData, predictionData]);

  return (
    <Box style={{ width: "100%", height: "100%" }}>
      <canvas ref={canvasRef} />
    </Box>
  );
};

export default DegradationChart;
